import { useMemo } from 'react';
import useAppRoutes from './useAppRoutes';

/**
 * Hook מותאם אישית לשיטוח (Flattening) של עץ הניתובים במערכת.
 * מכיוון ש-React Router v6 משתמש בנתיבים מקוננים (מאפיין children), 
 * רכיבים מסוימים כמו ה-SideBar או פונקציות חיפוש מתקשות למצוא ראוטים פנימיים.
 * * ה-Hook הזה לוקח את עץ הראוטים הראשי, שולף את כל הילדים החוצה ברקורסיה,
 * מחבר להם את כתובות האב, ומחזיר מערך חד-ממדי שממוין לפי רמת "ספציפיות".
 *
 * @returns {Array} מערך חד-ממדי וממוין של כל הניתובים (אבות וילדים).
 */
const useFlattenedRoutes = () => {
    const mainRoutes = useAppRoutes();

    return useMemo(() => {
        
        /**
         * פונקציה רקורסיבית שעוברת על עץ הניתובים והופכת אותו לרשימה שטוחה.
         * הפונקציה משרשרת את נתיב האב לנתיב הילד ומנקה בעיות תחביר (כמו לוכסנים כפולים).
         * * @param {Array} routes - מערך הניתובים לסריקה
         * @param {string} parentPath - הנתיב המצטבר של כל האבות (ברירת מחדל: מחרוזת ריקה)
         * @returns {Array} מערך שטוח
         */
        const flattenRoutesArray = (routes, parentPath = '') => {
            let flat = [];
            
            routes.forEach(route => {
                // 1. קביעת נתיב האלמנט (מטפל גם בראוטים של index: true שאין להם path משלהם)
                const currentPath = route.path ? route.path : '';
                
                // 2. הרכבת הנתיב המלא: אב + ילד
                const rawFullPath = `${parentPath}/${currentPath}`;
                
                // 3. ניקוי לוכסנים כפולים (//) והסרת לוכסן מיותר בסוף הנתיב
                const fullPath = rawFullPath.replace(/\/+/g, '/').replace(/\/$/, '');

                // 4. הוספת הראוט הנוכחי לרשימה עם הכתובת המלאה המעודכנת
                flat.push({ ...route, path: fullPath || '/' });

                // 5. אם יש לאלמנט ילדים (children), נכנסים אליהם ברקורסיה, 
                // ומעבירים להם את הנתיב הנוכחי כדי שישתמשו בו כנתיב האב שלהם.
                if (route.children && route.children.length > 0) {
                    flat = flat.concat(flattenRoutesArray(route.children, fullPath));
                }
            });
            
            return flat;
        };

        // שלב א': שיטוח העץ הראשי שמתקבל מ-useAppRoutes
        const flatRoutes = flattenRoutesArray(mainRoutes);

        // שלב ב': אלגוריתם מיון לפי ספציפיות (Specificity)
        // חשוב מאוד: המיון מבטיח שכאשר מחפשים התאמה ל-URL, הראוטר יעדיף
        // את הנתיב המדויק ביותר, לפני שייפול על נתיב כללי או נתיב דינמי.
        return flatRoutes.sort((a, b) => {
            const aPath = a.path || '';
            const bPath = b.path || '';

            // חוק 1: ניתובים כוללניים/גיבוי עם כוכבית (*) - תמיד נדחפים לסוף (הכי פחות ספציפיים)
            const aIsWildcard = aPath.includes('*');
            const bIsWildcard = bPath.includes('*');
            if (aIsWildcard !== bIsWildcard) return aIsWildcard ? 1 : -1;

            // חוק 2: פרמטרים דינמיים (:) - נתיב סטטי מדויק יקבל עדיפות ויופיע לפני נתיב דינמי
            const aIsDynamic = aPath.includes(':');
            const bIsDynamic = bPath.includes(':');
            if (aIsDynamic !== bIsDynamic) return aIsDynamic ? 1 : -1;

            // חוק 3: אורך הנתיב - אם שניהם דומים, נתיב ארוך ומפורט יותר (יותר /) מנצח ויופיע קודם
            return bPath.length - aPath.length;
        });
        
    }, [mainRoutes]); // מערך התלויות עודכן: התבססות רק על mainRoutes
};

export default useFlattenedRoutes;